/**
 * `Money` — importes en **centavos enteros de ARS**.
 * CLAUDE.md: nunca `number` con decimales, nunca `parseFloat` sobre plata.
 * Módulo puro: sin dependencias, usable en backend, web, app y agente.
 */
export const ZERO = 0;
/** Redondeo half-up sobre magnitud (simétrico): 0.5 → 1, −0.5 → −1. RN-CAJ-04. */
export function roundHalfUp(value) {
    return value < 0 ? -Math.round(-value) : Math.round(value);
}
export class MoneyError extends Error {
}
/** Construye un `Money` desde centavos ya enteros. Falla si no es entero finito. */
export function money(cents) {
    if (!Number.isFinite(cents) || !Number.isInteger(cents)) {
        throw new MoneyError(`Money debe ser un entero de centavos, recibí: ${cents}`);
    }
    if (!Number.isSafeInteger(cents)) {
        throw new MoneyError(`Money fuera del rango seguro: ${cents}`);
    }
    return cents;
}
/** Convierte pesos (posiblemente con decimales) a `Money`, redondeando half-up. */
export function toMoney(pesos) {
    if (!Number.isFinite(pesos)) {
        throw new MoneyError(`toMoney recibió un valor no finito: ${pesos}`);
    }
    return money(roundHalfUp(pesos * 100));
}
/**
 * Parsea "$20.000,50", "20.000", "20000,50" o "20000.50" a `Money`.
 *
 * La ambigüedad real del formato es-AR es el punto solo: en "20.000" es separador de
 * miles y en "20.50" es decimal. Regla adoptada, en este orden:
 * 1. Si hay coma, la coma es el decimal y los puntos son miles (es-AR puro).
 * 2. Si sólo hay puntos y el último grupo tiene EXACTAMENTE 3 dígitos, son miles.
 * 3. Si no, el punto es decimal (formato plano tipo "20000.50").
 *
 * Es un helper de entrada manual y de importación de planillas. El sistema no lo usa
 * para calcular precios: esos salen de `priceLists` (RN-CAJ-04).
 */
export function parseARS(input) {
    const cleaned = input.replace(/[^\d,.-]/g, "").trim();
    if (cleaned === "" || cleaned === "-")
        throw new MoneyError(`Importe ilegible: "${input}"`);
    const lastComma = cleaned.lastIndexOf(",");
    const lastDot = cleaned.lastIndexOf(".");
    let normalized;
    if (lastComma > -1) {
        normalized = cleaned.replace(/\./g, "").replace(",", ".");
    }
    else if (lastDot > -1) {
        const decimals = cleaned.length - lastDot - 1;
        normalized = decimals === 3 ? cleaned.replace(/\./g, "") : cleaned;
    }
    else {
        normalized = cleaned;
    }
    const parsed = Number(normalized);
    if (!Number.isFinite(parsed))
        throw new MoneyError(`Importe ilegible: "${input}"`);
    return toMoney(parsed);
}
/** Pesos como número decimal — SÓLO para mostrar o exportar, jamás para operar. */
export function toPesos(amount) {
    return amount / 100;
}
export function add(...amounts) {
    return money(amounts.reduce((acc, a) => acc + a, 0));
}
export function sub(a, b) {
    return money(a - b);
}
export function neg(a) {
    return money(-a);
}
export function sum(amounts) {
    return money(amounts.reduce((acc, a) => acc + a, 0));
}
/** Multiplica por una cantidad entera (unidades vendidas, cuotas). */
export function multiply(amount, qty) {
    if (!Number.isInteger(qty)) {
        throw new MoneyError(`multiply espera una cantidad entera, recibí: ${qty}`);
    }
    return money(amount * qty);
}
/**
 * Porcentaje sobre un importe con redondeo half-up al centavo (RN-CAJ-04).
 * `percentOf(toMoney(1000), 10) === toMoney(100)`.
 */
export function percentOf(amount, pct) {
    if (!Number.isFinite(pct))
        throw new MoneyError(`Porcentaje inválido: ${pct}`);
    return money(roundHalfUp((amount * pct) / 100));
}
/** Aplica un descuento porcentual: devuelve `{ discount, net }`. RN-CAJ-20. */
export function applyDiscountPct(amount, pct) {
    const discount = percentOf(amount, pct);
    return { discount, net: sub(amount, discount) };
}
/**
 * Compone descuentos porcentuales **en orden** sobre los conceptos de un cobro
 * (RN-PLA-05 / RN-PLA-20 / RN-PLA-21).
 *
 * - Una fuente **sin `chargeIds`** alcanza a todo el cobro: se aplica sobre el neto
 *   acumulado, igual que `applyDiscountPct` sobre el total.
 * - Una fuente **con `chargeIds`** alcanza sólo a esos conceptos (el descuento familiar
 *   sobre la cuota del plan de menor valor, el crédito de referido sobre una cuota): se
 *   aplica sobre lo que a cada uno le queda después de las fuentes anteriores. Una clave
 *   que no está entre los conceptos no descuenta nada.
 *
 * El backend y la tablet usan esta misma función: el número que se ve es el que se cobra.
 */
export function applyDiscountSources(lines, sources) {
    const base = sum(lines.map((line) => line.amount));
    let net = base;
    const lineNet = new Map(lines.map((line) => [line.key, line.amount]));
    const bySource = [];
    for (const source of sources) {
        if (!source.chargeIds) {
            const cut = percentOf(net, source.pct);
            net = sub(net, cut);
            for (const [key, value] of lineNet)
                lineNet.set(key, applyDiscountPct(value, source.pct).net);
            bySource.push(cut);
            continue;
        }
        let cut = ZERO;
        for (const key of source.chargeIds) {
            const value = lineNet.get(key);
            if (value === undefined)
                continue;
            const { discount, net: lineRest } = applyDiscountPct(value, source.pct);
            lineNet.set(key, lineRest);
            cut = add(cut, discount);
        }
        net = sub(net, cut);
        bySource.push(cut);
    }
    return { discount: sub(base, net), net, bySource };
}
/**
 * Reparte un total en `n` partes iguales sin perder centavos:
 * el resto se distribuye de a un centavo sobre las primeras cuotas (RN-PLA-14).
 */
export function splitInstallments(total, n) {
    if (!Number.isInteger(n) || n < 1)
        throw new MoneyError(`Cuotas inválidas: ${n}`);
    const sign = total < 0 ? -1 : 1;
    const abs = Math.abs(total);
    const base = Math.floor(abs / n);
    const remainder = abs - base * n;
    return Array.from({ length: n }, (_, i) => money(sign * (base + (i < remainder ? 1 : 0))));
}
const ARS_FORMATTER = new Intl.NumberFormat("es-AR", {
    style: "currency",
    currency: "ARS",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
});
/** "$ 20.000,00". Sólo presentación. */
export function formatARS(amount) {
    return ARS_FORMATTER.format(toPesos(amount));
}
/** Guardia de tipo para valores que vienen de la DB o del cliente. */
export function isMoney(value) {
    return typeof value === "number" && Number.isSafeInteger(value);
}
//# sourceMappingURL=money.js.map